import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, CalendarClock, HardDrive, Plus, ShieldCheck, Trash2 } from 'lucide-react';
import { PageHeader, TableSkeleton } from '@/components/shared/page';
import { Can } from '@/components/shared/can';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, Textarea } from '@/components/ui/misc';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { FormError } from '@/features/auth/auth-layout';
import { useCompanyOptions } from '@/features/crm/api';
import { useUserOptions } from '@/hooks/use-user-options';
import { ApiRequestError } from '@/lib/api';
import { formatDate, formatMoney } from '@/lib/format';
import { cn } from '@/lib/utils';
import {
  useAssets,
  useBusinessHours,
  useCannedResponses,
  useDeleteAsset,
  useDeleteCannedResponse,
  useSaveAsset,
  useSaveCannedResponse,
  useSaveSlaPolicy,
  useSlaCompliance,
  useSlaPolicies,
} from './api';
import {
  ASSET_TYPE_LABELS,
  TICKET_PRIORITY_LABELS,
  TICKET_STATUS_LABELS,
} from './labels';

const TABS = [
  ['sla', 'SLA policies'],
  ['hours', 'Working hours'],
  ['replies', 'Canned replies'],
  ['assets', 'Client assets'],
  ['compliance', 'SLA compliance'],
] as const;

const DAYS = [
  ['0', 'Sunday'],
  ['1', 'Monday'],
  ['2', 'Tuesday'],
  ['3', 'Wednesday'],
  ['4', 'Thursday'],
  ['5', 'Friday'],
  ['6', 'Saturday'],
] as const;

export function ServiceSettingsPage() {
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as (typeof TABS)[number][0]) ?? 'sla';

  return (
    <div>
      <PageHeader
        title="Service setup"
        description="What the desk promises, when it is open, and what the client owns."
      />

      <div className="mb-5 flex flex-wrap gap-1 border-b">
        {TABS.map(([key, label]) => (
          <button
            key={key}
            onClick={() => setParams({ tab: key })}
            className={
              tab === key
                ? 'border-b-2 border-primary px-3 py-2 text-sm font-medium'
                : 'px-3 py-2 text-sm text-muted-foreground hover:text-foreground'
            }
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'sla' && <SlaPolicies />}
      {tab === 'hours' && <WorkingHours />}
      {tab === 'replies' && <CannedReplies />}
      {tab === 'assets' && <AssetRegister />}
      {tab === 'compliance' && <Compliance />}
    </div>
  );
}

function SlaPolicies() {
  const { data: policies = [], isLoading } = useSlaPolicies();
  const { data: calendars = [] } = useBusinessHours();
  const save = useSaveSlaPolicy();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    name: '',
    appliesToPriority: '',
    firstResponseHours: 4,
    resolutionHours: 24,
    businessHoursId: '',
    priorityOrder: 100,
  });
  const [error, setError] = useState<string | null>(null);

  if (isLoading) return <TableSkeleton columns={5} />;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex-row items-start justify-between pb-3">
          <div>
            <CardTitle className="text-base">What the desk promises</CardTitle>
            <CardDescription>
              Targets are in <strong>working</strong> hours. A four-hour target on a ticket raised
              at 17:00 on a Thursday is due at noon on the next working day, not at 21:00 that
              night. Policies are checked in order and the first match wins; a contract&rsquo;s own
              policy always beats a generic rule.
            </CardDescription>
          </div>
          <Can permission="service.sla.update">
            <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
              <Plus className="h-4 w-4" />
              Add policy
            </Button>
          </Can>
        </CardHeader>
      </Card>

      {adding && (
        <Card>
          <CardContent className="space-y-3 p-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Name</Label>
                <Input
                  value={form.name}
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Applies to priority</Label>
                <Select
                  value={form.appliesToPriority}
                  onChange={(event) => setForm({ ...form, appliesToPriority: event.target.value })}
                >
                  <option value="">Any priority</option>
                  {Object.entries(TICKET_PRIORITY_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-4">
              <div className="space-y-1.5">
                <Label>First response (working h)</Label>
                <Input
                  type="number"
                  min={0.25}
                  step={0.25}
                  value={form.firstResponseHours}
                  onChange={(event) =>
                    setForm({ ...form, firstResponseHours: Number(event.target.value) })
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label>Resolution (working h)</Label>
                <Input
                  type="number"
                  min={0.25}
                  step={0.25}
                  value={form.resolutionHours}
                  onChange={(event) =>
                    setForm({ ...form, resolutionHours: Number(event.target.value) })
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label>Calendar</Label>
                <Select
                  value={form.businessHoursId}
                  onChange={(event) => setForm({ ...form, businessHoursId: event.target.value })}
                >
                  <option value="">Choose one</option>
                  {calendars.map((calendar) => (
                    <option key={calendar.id} value={calendar.id}>
                      {calendar.name}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Checked at position</Label>
                <Input
                  type="number"
                  min={1}
                  value={form.priorityOrder}
                  onChange={(event) =>
                    setForm({ ...form, priorityOrder: Number(event.target.value) })
                  }
                />
              </div>
            </div>

            <FormError message={error} />

            <div className="flex gap-2">
              <Button
                size="sm"
                disabled={save.isPending || !form.name || !form.businessHoursId}
                onClick={async () => {
                  setError(null);
                  try {
                    await save.mutateAsync({
                      name: form.name,
                      appliesToPriority: form.appliesToPriority || undefined,
                      firstResponseMinutes: Math.round(form.firstResponseHours * 60),
                      resolutionMinutes: Math.round(form.resolutionHours * 60),
                      businessHoursId: form.businessHoursId,
                      priorityOrder: form.priorityOrder,
                    });
                    setAdding(false);
                  } catch (caught) {
                    setError(
                      caught instanceof ApiRequestError ? caught.message : 'Could not save it.',
                    );
                  }
                }}
              >
                Save policy
              </Button>
              <Button size="sm" variant="outline" onClick={() => setAdding(false)}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="overflow-x-auto p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Policy</TableHead>
                <TableHead>Applies to</TableHead>
                <TableHead>First response</TableHead>
                <TableHead>Resolution</TableHead>
                <TableHead>Calendar</TableHead>
                <TableHead className="text-right">In use</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {policies.map((policy) => (
                <TableRow key={policy.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{policy.name}</span>
                      {policy.isDefault && (
                        <Badge variant="secondary" className="text-[10px]">
                          default
                        </Badge>
                      )}
                      {!policy.isActive && (
                        <Badge variant="outline" className="text-[10px]">
                          off
                        </Badge>
                      )}
                    </div>
                    {policy.description && (
                      <div className="text-xs text-muted-foreground">{policy.description}</div>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {policy.appliesToPriority
                      ? TICKET_PRIORITY_LABELS[policy.appliesToPriority]
                      : 'Any priority'}
                  </TableCell>
                  <TableCell className="text-sm">{policy.firstResponseLabel}</TableCell>
                  <TableCell className="text-sm">{policy.resolutionLabel}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {policy.businessHours.name}
                  </TableCell>
                  <TableCell className="tabular text-right text-sm text-muted-foreground">
                    {policy._count.tickets} tickets
                    {policy._count.contracts > 0 && `, ${policy._count.contracts} contracts`}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function WorkingHours() {
  const { data: calendars = [], isLoading } = useBusinessHours();

  if (isLoading) return <TableSkeleton columns={3} />;

  return (
    <div className="space-y-4">
      {calendars.map((calendar) => (
        <Card key={calendar.id}>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <CalendarClock className="h-4 w-4 text-muted-foreground" />
              {calendar.name}
              {calendar.isDefault && (
                <Badge variant="secondary" className="text-[10px]">
                  default
                </Badge>
              )}
            </CardTitle>
            <CardDescription>
              {calendar.timezone} · {calendar._count.slaPolicies}{' '}
              {calendar._count.slaPolicies === 1 ? 'policy uses' : 'policies use'} this calendar
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-2 sm:grid-cols-7">
              {DAYS.map(([key, label]) => {
                const windows = calendar.schedule?.[key] ?? [];
                return (
                  <div
                    key={key}
                    className={cn(
                      'rounded-lg border p-2.5 text-center',
                      windows.length === 0 && 'bg-muted/40',
                    )}
                  >
                    <div className="text-xs font-medium">{label.slice(0, 3)}</div>
                    {windows.length === 0 ? (
                      <div className="mt-1 text-[11px] text-muted-foreground">Closed</div>
                    ) : (
                      windows.map(([open, close]) => (
                        <div key={`${open}-${close}`} className="tabular mt-1 text-[11px]">
                          {open}–{close}
                        </div>
                      ))
                    )}
                  </div>
                );
              })}
            </div>

            {calendar.holidays.length > 0 && (
              <div>
                <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Holidays — the clock does not run on these
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {calendar.holidays.map((holiday) => (
                    <Badge key={holiday.id} variant="outline" className="text-[11px]">
                      {formatDate(holiday.date)} · {holiday.name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function CannedReplies() {
  const { data: replies = [], isLoading } = useCannedResponses();
  const save = useSaveCannedResponse();
  const remove = useDeleteCannedResponse();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', body: '', setStatus: '' });

  if (isLoading) return <TableSkeleton columns={2} />;

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Can permission="service.ticket.update">
          <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
            <Plus className="h-4 w-4" />
            Add reply
          </Button>
        </Can>
      </div>

      {adding && (
        <Card>
          <CardContent className="space-y-3 p-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Name</Label>
                <Input
                  value={form.name}
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Also set the status to</Label>
                <Select
                  value={form.setStatus}
                  onChange={(event) => setForm({ ...form, setStatus: event.target.value })}
                >
                  <option value="">Leave it alone</option>
                  {Object.entries(TICKET_STATUS_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>The reply</Label>
              <Textarea
                rows={4}
                value={form.body}
                onChange={(event) => setForm({ ...form, body: event.target.value })}
              />
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                disabled={save.isPending || form.name.length < 2 || form.body.length < 2}
                onClick={async () => {
                  await save.mutateAsync({
                    name: form.name,
                    body: form.body,
                    setStatus: form.setStatus || undefined,
                  });
                  setAdding(false);
                  setForm({ name: '', body: '', setStatus: '' });
                }}
              >
                Save reply
              </Button>
              <Button size="sm" variant="outline" onClick={() => setAdding(false)}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {replies.map((reply) => (
        <Card key={reply.id}>
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-medium">{reply.name}</h3>
                  {reply.setStatus && (
                    <Badge variant="secondary" className="text-[10px]">
                      sets to {TICKET_STATUS_LABELS[reply.setStatus].toLowerCase()}
                    </Badge>
                  )}
                </div>
                <p className="mt-1.5 whitespace-pre-line text-sm text-muted-foreground">
                  {reply.body}
                </p>
              </div>
              <Can permission="service.ticket.update">
                <button
                  className="shrink-0 text-muted-foreground hover:text-destructive"
                  onClick={() => remove.mutate(reply.id)}
                  aria-label="Delete"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </Can>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function AssetRegister() {
  const [expiring, setExpiring] = useState(false);
  const { data: assets = [], isLoading } = useAssets(expiring ? { expiringInDays: 60 } : {});
  const { data: companies = [] } = useCompanyOptions();
  const { data: users = [] } = useUserOptions();
  const save = useSaveAsset();
  const remove = useDeleteAsset();

  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    name: '',
    assetType: 'DOMAIN',
    companyId: '',
    contractId: '',
    provider: '',
    expiryDate: '',
    renewalCost: '',
    ownerId: '',
  });

  if (isLoading) return <TableSkeleton columns={5} />;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex-row items-start justify-between pb-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <HardDrive className="h-4 w-4 text-muted-foreground" />
              Client assets
            </CardTitle>
            <CardDescription>
              Domains, certificates and licences we are responsible for keeping alive. Alerts fire
              at 60, 30 and 7 days. Credentials are never stored here — only a pointer to a
              document with its own access control.
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant={expiring ? 'secondary' : 'outline'}
              onClick={() => setExpiring((value) => !value)}
            >
              Expiring soon
            </Button>
            <Can permission="service.asset.create">
              <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
                <Plus className="h-4 w-4" />
                Add
              </Button>
            </Can>
          </div>
        </CardHeader>
      </Card>

      {adding && (
        <Card>
          <CardContent className="space-y-3 p-4">
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="space-y-1.5">
                <Label>Name</Label>
                <Input
                  value={form.name}
                  placeholder="example.com"
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Type</Label>
                <Select
                  value={form.assetType}
                  onChange={(event) => setForm({ ...form, assetType: event.target.value })}
                >
                  {Object.entries(ASSET_TYPE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Client</Label>
                <Select
                  value={form.companyId}
                  onChange={(event) => setForm({ ...form, companyId: event.target.value })}
                >
                  <option value="">Ours, not a client&rsquo;s</option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-4">
              <div className="space-y-1.5">
                <Label>Provider</Label>
                <Input
                  value={form.provider}
                  onChange={(event) => setForm({ ...form, provider: event.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Expires</Label>
                <Input
                  type="date"
                  value={form.expiryDate}
                  onChange={(event) => setForm({ ...form, expiryDate: event.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Renewal cost</Label>
                <Input
                  inputMode="decimal"
                  placeholder="0.00"
                  value={form.renewalCost}
                  onChange={(event) => setForm({ ...form, renewalCost: event.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Owner</Label>
                <Select
                  value={form.ownerId}
                  onChange={(event) => setForm({ ...form, ownerId: event.target.value })}
                >
                  <option value="">Nobody yet</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                size="sm"
                disabled={save.isPending || form.name.length < 2}
                onClick={async () => {
                  await save.mutateAsync({
                    name: form.name,
                    assetType: form.assetType,
                    companyId: form.companyId || undefined,
                    provider: form.provider || undefined,
                    expiryDate: form.expiryDate || undefined,
                    renewalCost: form.renewalCost || undefined,
                    ownerId: form.ownerId || undefined,
                  });
                  setAdding(false);
                  setForm({
                    name: '',
                    assetType: 'DOMAIN',
                    companyId: '',
                    contractId: '',
                    provider: '',
                    expiryDate: '',
                    renewalCost: '',
                    ownerId: '',
                  });
                }}
              >
                Add asset
              </Button>
              <Button size="sm" variant="outline" onClick={() => setAdding(false)}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="overflow-x-auto p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Asset</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Provider</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead className="text-right">Renewal</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {assets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground">
                    Nothing in the register.
                  </TableCell>
                </TableRow>
              ) : (
                assets.map((asset) => (
                  <TableRow key={asset.id}>
                    <TableCell>
                      <div className="font-medium">{asset.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {ASSET_TYPE_LABELS[asset.assetType]}
                        {asset.hasStoredCredentials && ' · credentials on file'}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {asset.company?.name ?? 'Ours'}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {asset.provider ?? '—'}
                    </TableCell>
                    <TableCell>
                      {asset.expiryDate ? (
                        <span
                          className={cn(
                            'text-sm',
                            asset.expired
                              ? 'font-medium text-destructive'
                              : (asset.daysUntilExpiry ?? 999) <= 30
                                ? 'font-medium text-amber-600'
                                : '',
                          )}
                        >
                          {formatDate(asset.expiryDate)}
                          {asset.daysUntilExpiry !== null && (
                            <span className="ml-1.5 text-xs text-muted-foreground">
                              ({asset.expired
                                ? `${Math.abs(asset.daysUntilExpiry)}d ago`
                                : `in ${asset.daysUntilExpiry}d`})
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="text-sm text-muted-foreground">No expiry</span>
                      )}
                    </TableCell>
                    <TableCell className="tabular text-right text-sm">
                      {formatMoney(asset.renewalCost, asset.currency)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Can permission="service.asset.delete">
                        <button
                          className="text-muted-foreground hover:text-destructive"
                          onClick={() => remove.mutate(asset.id)}
                          aria-label="Delete"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </Can>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function Compliance() {
  const now = new Date();
  const [from, setFrom] = useState(
    new Date(now.getFullYear(), now.getMonth() - 2, 1).toISOString().slice(0, 10),
  );
  const [to, setTo] = useState(now.toISOString().slice(0, 10));
  const { data, isLoading } = useSlaCompliance({ from, to });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label className="text-xs">From</Label>
          <Input
            type="date"
            className="h-8"
            value={from}
            onChange={(event) => setFrom(event.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">To</Label>
          <Input
            type="date"
            className="h-8"
            value={to}
            onChange={(event) => setTo(event.target.value)}
          />
        </div>
      </div>

      {isLoading || !data ? (
        <TableSkeleton columns={4} />
      ) : data.total === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            No tickets were resolved in this window, so there is nothing to report on.
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <ShieldCheck className="h-4 w-4 text-primary" />
                Overall
              </CardTitle>
              <CardDescription>
                Only resolved tickets are counted — including live ones would let a bad month look
                good simply by leaving tickets open.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-3">
              <Metric label="Tickets resolved" value={String(data.total)} />
              <Metric
                label="First response met"
                value={
                  data.overall.responsePercent === null ? '—' : `${data.overall.responsePercent}%`
                }
                good={(data.overall.responsePercent ?? 0) >= 90}
              />
              <Metric
                label="Resolution met"
                value={
                  data.overall.resolutionPercent === null
                    ? '—'
                    : `${data.overall.resolutionPercent}%`
                }
                good={(data.overall.resolutionPercent ?? 0) >= 90}
              />
            </CardContent>
          </Card>

          <div className="grid gap-4 lg:grid-cols-2">
            <Breakdown title="By client" rows={data.byCompany} />
            <Breakdown title="By priority" rows={data.byPriority} />
            <Breakdown title="By agent" rows={data.byAgent} />
            <Breakdown title="By contract" rows={data.byContract} />
          </div>

          {data.breaches.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <AlertTriangle className="h-4 w-4 text-destructive" />
                  Breaches ({data.breaches.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="overflow-x-auto p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Ticket</TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead>Priority</TableHead>
                      <TableHead>Agent</TableHead>
                      <TableHead className="text-right">Over by</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.breaches.map((breach) => (
                      <TableRow key={breach.id}>
                        <TableCell className="font-mono text-xs">{breach.number}</TableCell>
                        <TableCell className="text-sm">{breach.company}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {TICKET_PRIORITY_LABELS[breach.priority]}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {breach.assignee ?? 'Unassigned'}
                        </TableCell>
                        <TableCell className="tabular text-right text-sm text-destructive">
                          {breach.overdueMinutes === null
                            ? '—'
                            : breach.overdueMinutes < 60
                              ? `${breach.overdueMinutes}m`
                              : `${Math.round(breach.overdueMinutes / 60)}h`}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

function Breakdown({
  title,
  rows,
}: {
  title: string;
  rows: { key: string; label: string; total: number; responsePercent: number | null; resolutionPercent: number | null }[];
}) {
  if (rows.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2.5">
        {rows.slice(0, 10).map((row) => (
          <div key={row.key} className="flex items-center justify-between text-sm">
            <span className="min-w-0 truncate">{row.label}</span>
            <span className="tabular ml-3 shrink-0 text-muted-foreground">
              {row.total} ·{' '}
              <span
                className={cn(
                  'font-medium',
                  (row.resolutionPercent ?? 0) >= 90
                    ? 'text-emerald-600'
                    : (row.resolutionPercent ?? 0) < 70
                      ? 'text-destructive'
                      : 'text-amber-600',
                )}
              >
                {row.resolutionPercent === null ? '—' : `${row.resolutionPercent}%`}
              </span>
            </span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function Metric({ label, value, good }: { label: string; value: string; good?: boolean }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div
        className={cn(
          'tabular mt-1 text-2xl font-bold tracking-tight',
          good === true && 'text-emerald-600',
          good === false && 'text-amber-600',
        )}
      >
        {value}
      </div>
    </div>
  );
}
